import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AlertTriangle,
    ChevronDown,
    FileDown,
    FileUp,
    FolderTree,
    LayoutGrid,
    List,
    Loader2,
    Package,
    PackagePlus,
    Plus,
    SlidersHorizontal,
    Sparkles,
    Trash2,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

import { Product } from '../../../modules/vendor/my-products/models/product.model';
import ConfirmDialog from '../../../common/components/ConfirmModal';
import { NetworkCatalogNudge } from '../../../modules/vendor/network/NetworkCatalogNudge';
import AuthClientStore from '../../../common/services/auth-client.service';
import { setLoading, toggleConfirmModal } from '../../../redux/vendor/ui/uiActions';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { resolveApplicationTypeLabel, dedupeApplicationTypes } from '../../../common/utils/application-type';
import { ProductsAsList } from '../../../modules/vendor/my-products/components/products-as-list.component';
import { ProductsAsGrid } from '../../../modules/vendor/my-products/components/products-as-grid.component';
import { PageContainer } from '../../../common/components/PageContainer';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { FilterChip } from '../../../components/design-system/common/FilterChip';
import { CatalogFacets } from '../../../modules/vendor/my-products/components/CatalogFacets';
import { AiSearchState, CatalogSearch } from '../../../modules/vendor/my-products/components/CatalogSearch';
import {
    AppearanceFilters,
    EMPTY_APPEARANCE_FILTERS,
    searchCatalogWithAi,
} from '../../../common/hooks/use-product-insights.hook';
import {
    CatalogFacets as CatalogFacetCounts,
    CatalogFilters,
    EMPTY_CATALOG_FACETS,
} from '../../../common/models/catalog-query.model';
import { useProductsApi } from '../../../common/hooks/use-products-api.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { LimitBadge } from '../../../components/LimitBadge';
import { UsageMeter } from '../../../components/design-system/plan/UsageMeter';
import { openUnlockDialog } from '../../../components/design-system/plan/plan-limit-store';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import { setProductsOrder } from '../../../redux/vendor/settings/settingsActions';
import { useVendorFamilies } from '../../../common/hooks/use-vendor-families.hook';
import { useResolveTranslation } from '../../../common/hooks/use-resolve-translation.hook';

/**
 * A filter value shown as a chip you can switch on and off.
 *
 * The catalogue has six of these groups; they were six copies of the same
 * forty lines of `sx`, which is how they drifted apart. One button, one rule:
 * a chosen value is filled with ink, the way selection reads everywhere else.
 */
/**
 * Was a local button with square corners that filled solid black when chosen.
 * Both were design-system violations — the radius token was never reached for,
 * and a filled active state is reserved for things that DO something. It is the
 * same chip the catalogue facets use, so it lives in the design system now.
 */
const FilterToggle = ({
    label,
    selected,
    onClick,
}: {
    label: string;
    selected: boolean;
    onClick: () => void;
}) => <FilterChip label={label} selected={selected} onClick={onClick} size="md" />;

/**
 * A multi-select filter that would be unreadable as a row of chips.
 *
 * Sizes and collections run to dozens of values, so they collapse into a menu
 * that reports how many are chosen rather than listing them.
 */
const MultiSelectFilter = ({
    placeholder,
    options,
    selected,
    onToggle,
}: {
    placeholder: string;
    options: string[];
    selected: string[];
    onToggle: (value: string) => void;
}) => (
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
            {/* h-8: the panel's own scale. It was h-9, a head taller than the
                chips it sits beside, which is the whole reason the row looked
                assembled from two different kits. */}
            <Button variant="outline" className="h-8 w-56 justify-between font-normal">
                <span className={cn('truncate', selected.length === 0 && 'text-muted-foreground')}>
                    {selected.length === 0 ? placeholder : `${selected.length} selected`}
                </span>
                <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            {options.length === 0 ? (
                <DropdownMenuLabel className="font-normal text-muted-foreground">—</DropdownMenuLabel>
            ) : (
                options.map((option) => (
                    <DropdownMenuCheckboxItem
                        key={option}
                        checked={selected.includes(option)}
                        onCheckedChange={() => onToggle(option)}
                        onSelect={(e) => e.preventDefault()}
                    >
                        {option}
                    </DropdownMenuCheckboxItem>
                ))
            )}
        </DropdownMenuContent>
    </DropdownMenu>
);

/** A chip in the "currently filtered by" row, with its own remove control. */
const ActiveFilterChip = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
    <Badge variant="outline" className="gap-1 py-1 pe-1 ps-2 font-normal">
        <span className="truncate">{label}</span>
        <button
            type="button"
            onClick={onRemove}
            className="grid size-4 place-items-center text-muted-foreground transition-colors hover:text-foreground"
            aria-label={`Remove ${label}`}
        >
            <X className="size-3" />
        </button>
    </Badge>
);

/** One headline number above the catalogue. */
const CatalogStat = ({
    icon,
    label,
    value,
    tone,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    tone?: 'warning' | 'danger';
}) => (
    <Card className="py-4">
        <CardContent className="relative px-4">
            <span
                className={cn(
                    'absolute end-4 top-0 [&_svg]:size-4',
                    tone === 'danger'
                        ? 'text-destructive'
                        : tone === 'warning'
                          ? 'text-warning'
                          : 'text-muted-foreground',
                )}
            >
                {icon}
            </span>
            <p className="mb-2 text-sm text-muted-foreground">{label}</p>
            <span className="font-heading text-3xl leading-none font-medium tracking-tight">{value}</span>
        </CardContent>
    </Card>
);

export const MyProducts = () => {
    const [t] = useTranslation();
    const { rt } = useResolveTranslation();
    const productsAPI = useProductsApi();
    const settingsAPI = useSettingsApi();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const { productsOrder } = useSelector((state: VendorState) => state.settings);
    const { families } = useVendorFamilies();
    const { sendAuthGuardedRequest } = useAuthContext();

    /*
     * The catalogue is a query, not a download.
     *
     * It used to fetch every product and filter, search and sort in the browser.
     * That stopped at scale — a 1000-product cap silently hid the rest of a
     * 5000-SKU vendor — so the server now answers one page at a time, plus the
     * counts and filter values around it (facets).
     */
    const [rows, setRows] = useState<Product[]>([]);
    const [rowsTotal, setRowsTotal] = useState(0);
    const [isLoadingRows, setIsLoadingRows] = useState(true);
    const [facets, setFacets] = useState<CatalogFacetCounts>(EMPTY_CATALOG_FACETS);
    const [facetsLoaded, setFacetsLoaded] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const reload = () => setReloadKey((k) => k + 1);

    // Family filter - defaults to first family when loaded
    const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
    const [appearanceFilters, setAppearanceFilters] = useState<AppearanceFilters>(EMPTY_APPEARANCE_FILTERS);

    // What is typed, and what the list reflects. Search runs on Enter.
    const [searchInput, setSearchInput] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');
    const [aiEnabled, setAiEnabled] = useState(false);
    const [aiSearch, setAiSearch] = useState<AiSearchState>({ uuids: null, understood: '', loading: false });

    /* Run on Enter, never as they type: each call is a model call and a charge. */
    const runAiSearch = async () => {
        setAiSearch({ uuids: null, understood: '', loading: true });
        try {
            const res = await searchCatalogWithAi(sendAuthGuardedRequest, searchInput);
            setAiSearch({ uuids: res.uuids, understood: res.understood, unavailable: res.unavailable, loading: false });
        } catch {
            setAiSearch({ uuids: [], understood: '', unavailable: t('PAGES.MY_PRODUCTS.AI_FAILED') as string, loading: false });
        }
    };
    const clearAiSearch = () => setAiSearch({ uuids: null, understood: '', loading: false });

    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
    const [modalProps, setModalProps] = useState({});
    const [previousViewMode, setPreviousViewMode] = useState<'list' | 'grid'>('list');
    const [showFilters, setShowFilters] = useState(false);
    const [addMenuAnchor, setAddMenuAnchor] = useState(null);
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportType, setExportType] = useState<'selection' | 'all'>('all');
    const [isExporting, setIsExporting] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Sorting: a previewed order not yet saved, and the full list a manual reorder needs.
    const [previewOrder, setPreviewOrder] = useState<string | null>(null);
    const [manualList, setManualList] = useState<Product[] | null>(null);
    const [isLoadingManualList, setIsLoadingManualList] = useState(false);

    // Selection for bulk operations. Kept as products, not ids: the rows on
    // screen change page by page, and a delete still needs each one's variants.
    const [selectedProducts, setSelectedProducts] = useState<Map<string, Product>>(new Map());
    const selectedProductIds = useMemo(() => new Set(selectedProducts.keys()), [selectedProducts]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Filter states
    const [activeFilters, setActiveFilters] = useState({
        visibility: 'all',
        productType: [],
        sizes: [],
        surface: [],
        collections: [],
        // 'complete' / 'draft' / 'all' — drafts are products created by
        // API-integration sync that still need vendor review. Default to showing
        // everything so vendors immediately see newly synced items.
        draft: 'all' as 'complete' | 'draft' | 'all',
    });

    // Export dialog states
    const [selectedFields, setSelectedFields] = useState({
        name: true,
        brand: true,
        color: true,
        pattern: true,
        sizeName: true,
        type: true,
        application_types: true,
        sku: true,
        isVisible: true
    });
    const [exportFormat, setExportFormat] = useState('csv');

    // Initialize selectedFamilyId when families are loaded
    useEffect(() => {
        if (families.length > 0 && !selectedFamilyId) {
            setSelectedFamilyId(families[0]._id);
        }
    }, [families, selectedFamilyId]);

    /** Everything the list is filtered by, as the server reads it. */
    const filters: CatalogFilters = useMemo(() => ({
        familyId: families.length > 1 ? selectedFamilyId || undefined : undefined,
        // An AI result wins outright: it was produced from the phrase, so also
        // searching the phrase would narrow the model's answer by rules it never saw.
        search: aiSearch.uuids !== null ? undefined : submittedSearch || undefined,
        uuids: aiSearch.uuids ?? undefined,
        visibility: activeFilters.visibility as CatalogFilters['visibility'],
        draft: activeFilters.draft,
        productTypes: activeFilters.productType,
        surfaces: activeFilters.surface,
        sizes: activeFilters.sizes,
        collections: activeFilters.collections,
        colorFamily: appearanceFilters.colorFamily,
        materialFamily: appearanceFilters.materialFamily,
        style: appearanceFilters.style,
        finish: appearanceFilters.finish,
    }), [families.length, selectedFamilyId, aiSearch.uuids, submittedSearch, activeFilters, appearanceFilters]);
    const filtersKey = JSON.stringify(filters);
    const effectiveOrder = previewOrder ?? productsOrder;

    // A new filter starts from the first page.
    useEffect(() => {
        setPage(0);
    }, [filtersKey]);

    // The page on screen.
    useEffect(() => {
        let cancelled = false;
        setIsLoadingRows(true);
        (async () => {
            try {
                if (aiSearch.uuids !== null) {
                    // An AI answer is short and ranked: fetch it whole, keep the
                    // model's order, and page through it here.
                    const { products } = aiSearch.uuids.length
                        ? await productsAPI.findProductsPage({ ...filters, page: 1, limit: Math.max(aiSearch.uuids.length, 1), order: effectiveOrder })
                        : { products: [] as Product[] };
                    const rank = new Map(aiSearch.uuids.map((u, i) => [u, i]));
                    const ranked = [...products].sort((a, b) => (rank.get(a.uuid) ?? 0) - (rank.get(b.uuid) ?? 0));
                    if (cancelled) return;
                    setRows(ranked.slice(page * rowsPerPage, (page + 1) * rowsPerPage));
                    setRowsTotal(ranked.length);
                } else {
                    const { products, total } = await productsAPI.findProductsPage({
                        ...filters,
                        page: page + 1,
                        limit: rowsPerPage,
                        order: effectiveOrder,
                    });
                    if (cancelled) return;
                    setRows(products);
                    setRowsTotal(total);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load products:', error);
                    setRows([]);
                    setRowsTotal(0);
                }
            } finally {
                if (!cancelled) setIsLoadingRows(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey, page, rowsPerPage, effectiveOrder, reloadKey, location.key]);

    // Counts, filter values and appearance chips around the list.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const next = await productsAPI.getCatalogFacets(filters);
                if (!cancelled) setFacets(next);
            } catch (error) {
                console.error('Failed to load catalogue facets:', error);
            } finally {
                if (!cancelled) setFacetsLoaded(true);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey, reloadKey, location.key]);

    const onDelete = (product) => {
        const siblingCount = product.siblings?.length || 0;
        const description = siblingCount > 0
            ? t("PAGES.MY_PRODUCTS.CONFIRM.DELETE_WITH_SIBLINGS_DESCRIPTION", { count: siblingCount })
            : t("PAGES.MY_PRODUCTS.CONFIRM.DELETE_DESCRIPTION");

        dispatch(toggleConfirmModal());
        setModalProps({
            title: t("PAGES.MY_PRODUCTS.CONFIRM.DELETE_TITLE"),
            description,
            handleAccepted: () => handleRemoveProduct(product)
        });
    };

    const handleRemoveProduct = async(product) => {
        dispatch(setLoading(true));
        const uuidsToDelete = [product.uuid];
        if (product.siblings) {
            product.siblings.forEach(sibling => uuidsToDelete.push(sibling.uuid));
        }
        await productsAPI.bulkDeleteProducts(uuidsToDelete);
        reload();
        dispatch(setLoading(false));
    };

    const handleSaveManualSort = async(orderUpdates) => {
        dispatch(setLoading(true));
        try {
            await Promise.all([
                productsAPI.bulkPatchProducts(orderUpdates),
                settingsAPI.updateConfig({preferedProductsOrder: "MANUAL"})
            ]);
            dispatch(setProductsOrder("MANUAL"));
        } finally {
            setManualList(null);
            setPreviewOrder(null);
            setPage(0);
            reload();
            dispatch(setLoading(false));
        }
    };

    const handleSaveDiscoverySort = async() => {
        dispatch(setLoading(true));
        await settingsAPI.updateConfig({preferedProductsOrder: "DISCOVERY"});
        dispatch(setProductsOrder("DISCOVERY"));
        setPreviewOrder(null);
        dispatch(setLoading(false));
    };

    const handleSaveSortConfig = async(configValue: string) => {
        dispatch(setLoading(true));
        await settingsAPI.updateConfig({preferedProductsOrder: configValue});
        dispatch(setProductsOrder(configValue));
        setPreviewOrder(null);
        setPage(0);
        dispatch(setLoading(false));
    };

    /** Show the list in an order before saving it — the server sorts. */
    const handleSortPreview = (configValue: string) => {
        setManualList(null);
        setPreviewOrder(configValue);
        setPage(0);
    };

    /**
     * A manual reorder needs every product of the family in hand: it saves a
     * position for each. The one place the catalogue loads a whole set, and only
     * when asked. Search and filters are ignored so positions stay global.
     */
    const handleManualSortStart = async () => {
        setIsLoadingManualList(true);
        setManualList([]);
        try {
            const all = await productsAPI.findAllMatching({
                familyId: filters.familyId,
                draft: activeFilters.draft,
                order: 'MANUAL',
            });
            setManualList(all);
        } catch (error) {
            console.error('Failed to load products for reordering:', error);
            setManualList(null);
        } finally {
            setIsLoadingManualList(false);
        }
    };

    const handleSortCancel = () => {
        setPreviewOrder(null);
        setManualList(null);
    };

    // Selection handlers for bulk operations
    const handleToggleSelectionMode = () => {
        setIsSelectionMode(!isSelectionMode);
        if (isSelectionMode) {
            setSelectedProducts(new Map());
        }
    };

    const handleSelectProduct = (productId: string) => {
        const next = new Map(selectedProducts);
        if (next.has(productId)) {
            next.delete(productId);
        } else {
            const product = rows.find(p => p.id === productId);
            if (product) next.set(productId, product);
        }
        setSelectedProducts(next);
    };

    const handleSelectAll = () => {
        const allSelected = rows.length > 0 && rows.every(p => selectedProducts.has(p.id));
        const next = new Map(selectedProducts);
        rows.forEach(p => (allSelected ? next.delete(p.id) : next.set(p.id, p)));
        setSelectedProducts(next);
    };

    // Every uuid a bulk delete removes: the selected products and their variants.
    const allProductIdsToDelete = useMemo(() => {
        const allUuids = new Set<string>();
        selectedProducts.forEach(product => {
            allUuids.add(product.uuid);
            product.siblings?.forEach(sibling => allUuids.add(sibling.uuid));
        });
        return allUuids;
    }, [selectedProducts]);

    const handleBulkDelete = async () => {
        if (allProductIdsToDelete.size === 0) return;

        setIsBulkDeleting(true);
        try {
            // Delete all selected products and their siblings
            await productsAPI.bulkDeleteProducts(Array.from(allProductIdsToDelete));
            setSelectedProducts(new Map());
            setIsSelectionMode(false);
            setShowBulkDeleteDialog(false);
            reload();
        } catch (error) {
            console.error('Bulk delete failed:', error);
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // Handle family tab change
    const handleFamilyTabChange = (newValue: string) => {
        setSelectedFamilyId(newValue);
        setPage(0);
    };

    const handleEditProduct = (product: Product) => {
        dispatch(setLoading(true));
        navigate(`/products/${product.uuid}/edit`);
    };

    const toggleViewMode = () => {
        setPreviousViewMode(viewMode);
        setViewMode((prevMode) => (prevMode === 'list' ? 'grid' : 'list'));
    };

    const handleProductVisibilityChange = async(product: Product) => {
        await productsAPI.findOneAndUpdate(product.uuid, {
            isVisible: !product.isVisible
        });
        reload();
    };

    const handleAddNewProduct = () => {
        navigate('/products/new');
        setAddMenuAnchor(null);
    };

    const handleImportProducts = () => {
        navigate('/products/import');
        setAddMenuAnchor(null);
    };

    const handleAddUsingAI = () => {
        setAddMenuAnchor(null);
        navigate('/products/new', { state: { aiMode: true } });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Filter values, as the server counted them across the catalogue.
    const getUniqueProductTypes = () => facets.productTypes;
    const getUniqueSizes = () => facets.sizes;
    const getUniqueSurfaces = () => dedupeApplicationTypes(facets.surfaces);
    const getUniqueCollections = () => facets.collections;

    // Export handlers
    const handleExportClick = (event) => {
        if (hasActiveFilters()) {
            setExportMenuAnchor(event.currentTarget);
        } else {
            setExportType('all');
            setShowExportDialog(true);
        }
    };

    const handleExportMenuSelect = (type: 'selection' | 'all') => {
        setExportType(type);
        setExportMenuAnchor(null);
        setShowExportDialog(true);
    };

    const handleFieldToggle = (field: string) => {
        setSelectedFields({
            ...selectedFields,
            [field]: !selectedFields[field]
        });
    };

    /* An export is a deliberate whole-set action, so it fetches the set then. */
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const productsToExport = exportType === 'selection'
                ? await productsAPI.findAllMatching({ ...filters, order: effectiveOrder })
                : await productsAPI.findAllMatching({ draft: activeFilters.draft, order: effectiveOrder });
            const fields = Object.keys(selectedFields).filter(key => selectedFields[key]);

            // Create data array
            const data = productsToExport.map(product => {
                const row = {};
                fields.forEach(field => {
                    if (field === 'application_types') {
                        row[field] = product[field]?.join(', ') || '';
                    } else if (field === 'isVisible') {
                        row[field] = product[field] ? 'Active' : 'Inactive';
                    } else {
                        row[field] = product[field] || '';
                    }
                });
                return row;
            });

            if (exportFormat === 'csv') {
                exportToCSV(data, fields);
            } else if (exportFormat === 'json') {
                exportToJSON(data);
            }

            setShowExportDialog(false);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const exportToCSV = (data, fields) => {
        const headers = fields.map(field => t(`EXPORT.FIELDS.${field.toUpperCase()}`)).join(',');
        const rows = data.map(row =>
            fields.map(field => `"${row[field] || ''}"`).join(',')
        ).join('\n');

        const csv = `${headers}\n${rows}`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `products_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const exportToJSON = (data) => {
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `products_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
    };

    // Remove individual filter
    const removeFilter = (filterType: string, value?: string) => {
        const newFilters = { ...activeFilters };

        if (filterType === 'visibility') {
            newFilters.visibility = 'all';
        } else if (filterType === 'draft') {
            newFilters.draft = 'all';
        } else if (value) {
            newFilters[filterType] = newFilters[filterType].filter(v => v !== value);
        }

        setActiveFilters(newFilters);
    };

    // Check if any filters are active
    const hasActiveFilters = () => {
        return activeFilters.visibility !== 'all' ||
               activeFilters.draft !== 'all' ||
               activeFilters.productType.length > 0 ||
               activeFilters.sizes.length > 0 ||
               activeFilters.surface.length > 0 ||
               activeFilters.collections.length > 0;
    };

    // Family tab counts, under the current filters and search.
    const getFilteredCountByFamily = (familyId: string): number => facets.familyCounts[familyId] ?? 0;

    // Total SKUs including all variants, counted on the server.
    const totalProducts = facets.skuCount;
    // Whether the vendor has a catalogue at all — not whether this filter found anything.
    const hasCatalog = facets.skuCount > 0;

    // Plan-driven limit: vendor override > plan.limits.maxProducts > legacy fallback (50)
    // -1 from getLimit means "unlimited"; we treat that as a very large number so the
    // existing percentage-based UI degrades cleanly (always 0%, button never disabled).
    const { getLimit } = useVendorPlan();
    const planMaxProducts = getLimit('maxProducts');
    const isUnlimitedProducts = planMaxProducts === -1;
    const maxProducts = isUnlimitedProducts ? Number.POSITIVE_INFINITY : (planMaxProducts > 0 ? planMaxProducts : 50);
    const productLimitPercentage = isUnlimitedProducts ? 0 : (totalProducts / maxProducts) * 100;
    const maxProductsDisplay = isUnlimitedProducts ? '∞' : String(maxProducts);

    const atLimit = productLimitPercentage >= 100;
    const nearLimit = productLimitPercentage >= 80;

    /*
     * At the limit the add actions still answer: they say why they cannot, and
     * how to get past it, instead of sitting there greyed out.
     */
    const unlessAtLimit = (go: () => void) => () => {
        if (!atLimit) return go();
        setAddMenuAnchor(null);
        openUnlockDialog({ quotaType: 'maxProducts', current: totalProducts, limit: maxProducts });
    };

    const allOnPageSelected = rows.length > 0 && rows.every((p) => selectedProducts.has(p.id));
    const someOnPageSelected = rows.some((p) => selectedProducts.has(p.id));

    const toggleInList = (list: string[], value: string) =>
        list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

    return (
        <PageContainer
            breadcrumbs={[
                { label: t('SIDEBAR.PRODUCTS.TITLE') },
                { label: t('SIDEBAR.PRODUCTS.CATALOG') },
            ]}
        >
            {hasCatalog && (
                <>
                    {/* The Ardealy Network: how much of this catalogue is already in it. Silent when nothing to say; Ardealy support only for now (see the sidebar). */}
                    {AuthClientStore.isSupportSession() && <NetworkCatalogNudge />}

                    {/*
                      The quota, only when it is nearly a problem.
                      
                      At 40 of 500 it is a fact nobody needs; at 470 it is the
                      most important thing on the page. It used to sit here
                      permanently beside two cards reading "—", which were
                      placeholders that shipped and never got wired.
                    */}
                    {(nearLimit || atLimit) && (
                        <div className="mb-6">
                            <CatalogStat
                                icon={<Package />}
                                label={t('PAGES.MY_PRODUCTS.TOTAL_PRODUCTS')}
                                value={`${totalProducts} / ${maxProductsDisplay}`}
                                tone={atLimit ? 'danger' : 'warning'}
                            />
                        </div>
                    )}

                    <CatalogFacets
                        appearance={facets.appearance}
                        total={facets.total}
                        value={appearanceFilters}
                        shown={rowsTotal}
                        onChange={setAppearanceFilters}
                    />

                    {/* Search and actions */}
                    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-1 flex-wrap items-center gap-2">
                            <CatalogSearch
                                value={searchInput}
                                onChange={setSearchInput}
                                submitted={aiSearch.uuids !== null ? searchInput : submittedSearch}
                                onSubmit={(text) => {
                                    setSubmittedSearch(text);
                                    // Enter on an unchanged search still refreshes the list.
                                    if (text === submittedSearch) reload();
                                }}
                                searching={isLoadingRows && !!submittedSearch}
                                aiEnabled={aiEnabled}
                                onAiEnabledChange={setAiEnabled}
                                ai={aiSearch}
                                onRunAi={runAiSearch}
                                onClearAi={clearAiSearch}
                            />

                            <Button
                                variant={hasActiveFilters() ? 'default' : 'outline'}
                                className="h-9 font-normal"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <SlidersHorizontal className="size-4" />
                                {t('PAGES.MY_PRODUCTS.FILTERS')}
                            </Button>

                            <Button variant="outline" className="h-9 font-normal" onClick={() => navigate('/products/categories')}>
                                <FolderTree className="size-4" />
                                <span className="hidden sm:inline">{t('SIDEBAR.PRODUCTS.CATEGORIES')}</span>
                            </Button>

                            {/* Not `compact`: that is text-xs, and this sits in a
                                row of text-sm buttons where it read as a footnote
                                rather than a peer. */}
                            {isUnlimitedProducts ? (
                                <LimitBadge
                                    current={totalProducts}
                                    limit={planMaxProducts}
                                    label={t('PLANS.LIMIT_BADGE.PRODUCTS', 'products')}
                                    className="h-9"
                                />
                            ) : (
                                // A finite catalogue (Free: 10) reads as a meter, with the way past it beside the number.
                                <UsageMeter
                                    variant="inline"
                                    limitKey="maxProducts"
                                    label={t('PLANS.LIMIT_BADGE.PRODUCTS', 'products')}
                                    current={totalProducts}
                                    limit={maxProducts}
                                />
                            )}

                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                variant={isSelectionMode ? 'default' : 'outline'}
                                className="h-9 font-normal"
                                onClick={handleToggleSelectionMode}
                            >
                                {isSelectionMode ? t('PAGES.MY_PRODUCTS.CANCEL_SELECTION') : t('PAGES.MY_PRODUCTS.SELECT')}
                            </Button>

                            {/*
                              Export asks which set only when a filter is on — otherwise
                              "these" and "all" are the same list and the menu is noise.
                              A disabled DropdownMenuTrigger would swallow the click, so
                              the no-filter case is a plain button, not a disabled one.
                            */}
                            {hasActiveFilters() ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="h-9 font-normal">
                                            <FileDown className="size-4" />
                                            <span className="hidden sm:inline">{t('PAGES.MY_PRODUCTS.EXPORT')}</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-60">
                                        <DropdownMenuItem onClick={() => handleExportMenuSelect('selection')}>
                                            <FileDown className="size-4" />
                                            {t('PAGES.MY_PRODUCTS.EXPORT_SELECTION')} ({rowsTotal})
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleExportMenuSelect('all')}>
                                            <FileDown className="size-4" />
                                            {t('PAGES.MY_PRODUCTS.EXPORT_ALL')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Button variant="outline" className="h-9 font-normal" onClick={handleExportClick}>
                                    <FileDown className="size-4" />
                                    <span className="hidden sm:inline">{t('PAGES.MY_PRODUCTS.EXPORT')}</span>
                                </Button>
                            )}

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button className="h-9 font-normal">
                                        <Plus className="size-4" />
                                        <span className="hidden sm:inline">{t('PAGES.MY_PRODUCTS.ADD_NEW')}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64">
                                    <DropdownMenuItem onClick={unlessAtLimit(handleAddNewProduct)} data-at-limit={atLimit || undefined}>
                                        <PackagePlus className="size-4" />
                                        {t('PAGES.MY_PRODUCTS.ADD_SINGLE_PRODUCT')}
                                        {atLimit && <span className="ms-auto text-[11px] text-muted-foreground">{t('FREE_PLAN.PRODUCTS.UNLOCK')}</span>}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={unlessAtLimit(handleImportProducts)} data-at-limit={atLimit || undefined}>
                                        <FileUp className="size-4" />
                                        {t('PAGES.MY_PRODUCTS.IMPORT_FROM_FILE')}
                                        {atLimit && <span className="ms-auto text-[11px] text-muted-foreground">{t('FREE_PLAN.PRODUCTS.UNLOCK')}</span>}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={unlessAtLimit(handleAddUsingAI)} data-at-limit={atLimit || undefined}>
                                        <Sparkles className="size-4" />
                                        {t('PAGES.MY_PRODUCTS.ADD_USING_AI')}
                                        {atLimit ? (
                                            <span className="ms-auto text-[11px] text-muted-foreground">{t('FREE_PLAN.PRODUCTS.UNLOCK')}</span>
                                        ) : (
                                            <Badge variant="outline" className="ms-auto text-[10px] font-normal uppercase">
                                                Beta
                                            </Badge>
                                        )}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>

                    {/* Filters */}
                    {showFilters && (
                        <Card className="mb-6">
                            <CardContent className="flex flex-col gap-5">
                                <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
                                    {/* Status — a server-side filter: drafts come from API sync */}
                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                                            {t('PAGES.MY_PRODUCTS.FILTERS_STATUS', 'Status')}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(['complete', 'draft', 'all'] as const).map((status) => (
                                                <FilterToggle
                                                    key={status}
                                                    label={t(
                                                        'PAGES.MY_PRODUCTS.STATUS_' + status.toUpperCase(),
                                                        status === 'complete' ? 'Complete' : status === 'draft' ? 'Drafts' : 'All',
                                                    )}
                                                    selected={activeFilters.draft === status}
                                                    onClick={() => setActiveFilters({ ...activeFilters, draft: status })}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                                            {t('PAGES.MY_PRODUCTS.FILTERS_VISIBILITY')}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {['all', 'visible', 'hidden'].map((vis) => (
                                                <FilterToggle
                                                    key={vis}
                                                    label={t('PAGES.MY_PRODUCTS.' + vis.toUpperCase())}
                                                    selected={activeFilters.visibility === vis}
                                                    onClick={() => setActiveFilters({ ...activeFilters, visibility: vis })}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                                            {t('PAGES.MY_PRODUCTS.FILTERS_PRODUCT_TYPE')}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {getUniqueProductTypes().map((type) => (
                                                <FilterToggle
                                                    key={type}
                                                    label={t('TYPES.' + type.toUpperCase(), { defaultValue: type })}
                                                    selected={activeFilters.productType.includes(type)}
                                                    onClick={() =>
                                                        setActiveFilters({
                                                            ...activeFilters,
                                                            productType: toggleInList(activeFilters.productType, type),
                                                        })
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                                            {t('PAGES.MY_PRODUCTS.FILTERS_SURFACE')}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {getUniqueSurfaces().map((surface) => (
                                                <FilterToggle
                                                    key={surface}
                                                    label={resolveApplicationTypeLabel(t, surface)}
                                                    selected={activeFilters.surface.includes(surface)}
                                                    onClick={() =>
                                                        setActiveFilters({
                                                            ...activeFilters,
                                                            surface: toggleInList(activeFilters.surface, surface),
                                                        })
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                                            {t('PAGES.MY_PRODUCTS.FILTERS_SIZE')}
                                        </p>
                                        <MultiSelectFilter
                                            placeholder={t('PAGES.MY_PRODUCTS.SELECT_SIZES')}
                                            options={getUniqueSizes()}
                                            selected={activeFilters.sizes}
                                            onToggle={(size) =>
                                                setActiveFilters({ ...activeFilters, sizes: toggleInList(activeFilters.sizes, size) })
                                            }
                                        />
                                    </div>

                                    <div>
                                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                                            {t('PAGES.MY_PRODUCTS.FILTERS_COLLECTION')}
                                        </p>
                                        <MultiSelectFilter
                                            placeholder={t('PAGES.MY_PRODUCTS.SELECT_COLLECTIONS')}
                                            options={getUniqueCollections()}
                                            selected={activeFilters.collections}
                                            onToggle={(collection) =>
                                                setActiveFilters({
                                                    ...activeFilters,
                                                    collections: toggleInList(activeFilters.collections, collection),
                                                })
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        className="h-9 font-normal"
                                        onClick={() =>
                                            setActiveFilters({
                                                visibility: 'all',
                                                productType: [],
                                                sizes: [],
                                                surface: [],
                                                collections: [],
                                                draft: 'all',
                                            })
                                        }
                                    >
                                        {t('PAGES.MY_PRODUCTS.CLEAR_FILTERS')}
                                    </Button>
                                    <Button variant="outline" className="h-9 font-normal" onClick={() => setShowFilters(false)}>
                                        {t('PAGES.MY_PRODUCTS.CLOSE_FILTERS')}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* What is currently filtered */}
                    {hasActiveFilters() && (
                        <div className="mb-6 flex flex-wrap gap-1.5">
                            {activeFilters.visibility !== 'all' && (
                                <ActiveFilterChip
                                    label={`${t('PAGES.MY_PRODUCTS.FILTERS_VISIBILITY')}: ${t('PAGES.MY_PRODUCTS.' + activeFilters.visibility.toUpperCase())}`}
                                    onRemove={() => removeFilter('visibility')}
                                />
                            )}
                            {activeFilters.draft !== 'all' && (
                                <ActiveFilterChip
                                    label={`${t('PAGES.MY_PRODUCTS.FILTERS_STATUS', 'Status')}: ${t('PAGES.MY_PRODUCTS.STATUS_' + activeFilters.draft.toUpperCase(), activeFilters.draft === 'draft' ? 'Drafts' : 'Complete')}`}
                                    onRemove={() => removeFilter('draft')}
                                />
                            )}
                            {activeFilters.productType.map((type) => (
                                <ActiveFilterChip
                                    key={type}
                                    label={`${t('PAGES.MY_PRODUCTS.FILTERS_PRODUCT_TYPE')}: ${t('TYPES.' + type.toUpperCase(), { defaultValue: type })}`}
                                    onRemove={() => removeFilter('productType', type)}
                                />
                            ))}
                            {activeFilters.surface.map((surface) => (
                                <ActiveFilterChip
                                    key={surface}
                                    label={`${t('PAGES.MY_PRODUCTS.FILTERS_SURFACE')}: ${resolveApplicationTypeLabel(t, surface)}`}
                                    onRemove={() => removeFilter('surface', surface)}
                                />
                            ))}
                            {activeFilters.sizes.map((size) => (
                                <ActiveFilterChip
                                    key={size}
                                    label={`${t('PAGES.MY_PRODUCTS.FILTERS_SIZE')}: ${size}`}
                                    onRemove={() => removeFilter('sizes', size)}
                                />
                            ))}
                            {activeFilters.collections.map((collection) => (
                                <ActiveFilterChip
                                    key={collection}
                                    label={`${t('PAGES.MY_PRODUCTS.FILTERS_COLLECTION')}: ${collection}`}
                                    onRemove={() => removeFilter('collections', collection)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Selection bar */}
                    {isSelectionMode && (
                        <Card className="mb-6">
                            <CardContent className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <Label className="flex items-center gap-2 text-sm font-normal">
                                        <Checkbox
                                            checked={allOnPageSelected ? true : someOnPageSelected ? 'indeterminate' : false}
                                            onCheckedChange={handleSelectAll}
                                        />
                                        {t('PAGES.MY_PRODUCTS.SELECT_ALL_PAGE')}
                                    </Label>
                                    <span className="text-sm text-muted-foreground">
                                        {t('PAGES.MY_PRODUCTS.SELECTED_COUNT', { count: selectedProductIds.size })}
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        className="h-9 font-normal"
                                        onClick={() => setSelectedProducts(new Map())}
                                        disabled={selectedProductIds.size === 0}
                                    >
                                        {t('PAGES.MY_PRODUCTS.CLEAR_SELECTION')}
                                    </Button>
                                    {/* Outlined, not filled: a destructive action should not be the loudest thing on the row. */}
                                    <Button
                                        variant="outline"
                                        className="h-9 font-normal border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                        onClick={() => setShowBulkDeleteDialog(true)}
                                        disabled={selectedProductIds.size === 0}
                                    >
                                        <Trash2 className="size-4" />
                                        {t('PAGES.MY_PRODUCTS.DELETE_SELECTED')} ({selectedProductIds.size})
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/*
                      What you are looking at, and how you are looking at it.
                      
                      The view toggle used to sit at the far end of the action
                      cluster, among things that DO something — export, select,
                      add. It changes how this list is drawn, which is the same
                      kind of choice as which family you are in, so it belongs
                      on this row instead.
                      
                      The row renders even with one family, since the toggle
                      still has to live somewhere; `ms-auto` keeps it right
                      whether the tabs are there or not.
                    */}
                    <div className="mb-6 flex flex-wrap items-center gap-3">
                        {families.length > 1 && selectedFamilyId && (
                            <Tabs value={selectedFamilyId} onValueChange={handleFamilyTabChange}>
                                <TabsList className="h-9 flex-wrap">
                                    {families.map((family) => (
                                        <TabsTrigger key={family._id} value={family._id} className="gap-1.5 px-3 text-sm">
                                            {rt(family.name)}
                                            <span className="text-muted-foreground">{getFilteredCountByFamily(family._id)}</span>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>
                        )}

                        <ToggleGroup
                            type="single"
                            value={viewMode}
                            onValueChange={(v) => v && v !== viewMode && toggleViewMode()}
                            variant="outline"
                            className="ms-auto h-9"
                        >
                            <ToggleGroupItem value="list" aria-label={t('PAGES.MY_PRODUCTS.VIEW_LIST', 'List view')}>
                                <List className="size-4" />
                            </ToggleGroupItem>
                            <ToggleGroupItem value="grid" aria-label={t('PAGES.MY_PRODUCTS.VIEW_GRID', 'Grid view')}>
                                <LayoutGrid className="size-4" />
                            </ToggleGroupItem>
                        </ToggleGroup>
                    </div>

                    <div key={viewMode} className={cn('transition-opacity', isLoadingRows && 'opacity-60')}>
                        {viewMode === 'list' ? (
                            <ProductsAsList
                                filteredProducts={manualList ?? rows}
                                totalCount={manualList ? manualList.length : rowsTotal}
                                handleEditProduct={handleEditProduct}
                                handleProductVisibilityChange={handleProductVisibilityChange}
                                handleSaveManualSort={handleSaveManualSort}
                                handleSaveDiscoverySort={handleSaveDiscoverySort}
                                handleSaveSortConfig={handleSaveSortConfig}
                                onSortPreview={handleSortPreview}
                                onManualSortStart={handleManualSortStart}
                                onSortCancel={handleSortCancel}
                                sortLoading={isLoadingManualList}
                                onDelete={onDelete}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                handleChangePage={handleChangePage}
                                handleChangeRowsPerPage={handleChangeRowsPerPage}
                                isSelectionMode={isSelectionMode}
                                selectedProductIds={selectedProductIds}
                                onSelectProduct={handleSelectProduct}
                                selectedFamilyId={selectedFamilyId}
                                productsOrder={productsOrder}
                            />
                        ) : (
                            <ProductsAsGrid
                                filteredProducts={rows}
                                totalCount={rowsTotal}
                                handleEditProduct={handleEditProduct}
                                handleProductVisibilityChange={handleProductVisibilityChange}
                                handleSaveManualSort={handleSaveManualSort}
                                onDelete={onDelete}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                handleChangePage={handleChangePage}
                                handleChangeRowsPerPage={handleChangeRowsPerPage}
                                isSelectionMode={isSelectionMode}
                                selectedProductIds={selectedProductIds}
                                onSelectProduct={handleSelectProduct}
                            />
                        )}
                    </div>

                    {/* A search or filter that found nothing is not an empty catalogue. */}
                    {!isLoadingRows && rowsTotal === 0 && !manualList && (
                        <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center">
                            <p className="text-sm font-medium">{t('PAGES.MY_PRODUCTS.NO_MATCHES')}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{t('PAGES.MY_PRODUCTS.NO_MATCHES_DESCRIPTION')}</p>
                        </div>
                    )}

                    <ConfirmDialog {...modalProps} />
                </>
            )}

            {/* Loading */}
            {!facetsLoaded && (
                <div className="flex min-h-[60vh] items-center justify-center">
                    <Loader2 className="size-7 animate-spin text-muted-foreground" />
                </div>
            )}

            {/* Empty */}
            {facetsLoaded && !hasCatalog && (
                <div className="mt-8 flex min-h-[40vh] flex-col items-center justify-center border border-dashed p-8 text-center">
                    <Package className="mb-4 size-12 text-muted-foreground/40" />
                    <h2 className="font-heading mb-1 text-xl font-medium tracking-tight">
                        {t('PAGES.MY_PRODUCTS.NO_PRODUCTS')}
                    </h2>
                    <p className="mb-6 max-w-lg text-sm text-muted-foreground">
                        {t('PAGES.MY_PRODUCTS.NO_PRODUCTS_DESCRIPTION')}
                    </p>
                    <Button onClick={handleAddNewProduct}>
                        <Plus className="size-4" />
                        {t('PAGES.MY_PRODUCTS.ADD_FIRST_PRODUCT')}
                    </Button>
                </div>
            )}

            {/* Export */}
            <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{t('PAGES.MY_PRODUCTS.EXPORT_PRODUCTS')}</DialogTitle>
                        <DialogDescription>
                            {exportType === 'selection'
                                ? t('PAGES.MY_PRODUCTS.EXPORTING_FILTERED', { count: rowsTotal })
                                : t('PAGES.MY_PRODUCTS.EXPORTING_ALL', { count: facets.total })}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-6 sm:grid-cols-[2fr_1fr]">
                        <div>
                            <p className="mb-2 text-xs font-medium text-muted-foreground">
                                {t('PAGES.MY_PRODUCTS.SELECT_FIELDS')}
                            </p>
                            <div className="grid grid-cols-2 gap-2 border p-3">
                                {Object.keys(selectedFields).map((field) => (
                                    <Label key={field} className="flex items-center gap-2 text-xs font-normal">
                                        <Checkbox
                                            checked={selectedFields[field]}
                                            onCheckedChange={() => handleFieldToggle(field)}
                                        />
                                        {t(`EXPORT.FIELDS.${field.toUpperCase()}`)}
                                    </Label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="mb-2 text-xs font-medium text-muted-foreground">
                                {t('PAGES.MY_PRODUCTS.EXPORT_FORMAT')}
                            </p>
                            <RadioGroup value={exportFormat} onValueChange={setExportFormat} className="gap-2 border p-3">
                                <Label className="flex items-center gap-2 text-xs font-normal">
                                    <RadioGroupItem value="csv" />
                                    CSV (.csv)
                                </Label>
                                <Label className="flex items-center gap-2 text-xs font-normal">
                                    <RadioGroupItem value="json" />
                                    JSON (.json)
                                </Label>
                            </RadioGroup>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowExportDialog(false)}>
                            {t('PAGES.MY_PRODUCTS.CANCEL')}
                        </Button>
                        <Button onClick={handleExport} disabled={isExporting || Object.values(selectedFields).every((v) => !v)}>
                            {isExporting && <Loader2 className="size-4 animate-spin" />}
                            {isExporting ? t('PAGES.MY_PRODUCTS.EXPORT_PREPARING') : t('PAGES.MY_PRODUCTS.EXPORT')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk delete */}
            <Dialog
                open={showBulkDeleteDialog}
                onOpenChange={(open) => !isBulkDeleting && setShowBulkDeleteDialog(open)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('PAGES.MY_PRODUCTS.BULK_DELETE_TITLE')}</DialogTitle>
                        <DialogDescription>
                            {t('PAGES.MY_PRODUCTS.BULK_DELETE_DESCRIPTION', { count: selectedProductIds.size })}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Deleting a product takes its variants with it — say so before, not after. */}
                    {allProductIdsToDelete.size > selectedProductIds.size && (
                        <p className="text-xs text-muted-foreground">
                            {t('PAGES.MY_PRODUCTS.BULK_DELETE_SIBLINGS_NOTE', {
                                siblingsCount: allProductIdsToDelete.size - selectedProductIds.size,
                                totalCount: allProductIdsToDelete.size,
                            })}
                        </p>
                    )}

                    <Alert variant="destructive">
                        <AlertTriangle />
                        <AlertDescription>{t('PAGES.MY_PRODUCTS.BULK_DELETE_WARNING')}</AlertDescription>
                    </Alert>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)} disabled={isBulkDeleting}>
                            {t('COMMON.CANCEL')}
                        </Button>
                        <Button
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                        >
                            {isBulkDeleting && <Loader2 className="size-4 animate-spin" />}
                            {isBulkDeleting ? t('PAGES.MY_PRODUCTS.DELETING') : t('PAGES.MY_PRODUCTS.CONFIRM_DELETE')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default MyProducts;
